import React from 'react';
import TopNav from '../components/TopNav';

const TermsOfService: React.FC = () => {
  return (
    <div className="min-h-screen bg-neutral-950">
      <TopNav />
      <div className="container mx-auto px-4 py-8">
        <div className="max-w-4xl mx-auto">
          <div className="bg-neutral-900 rounded-lg p-8">
            <h1 className="text-3xl font-bold text-white mb-2">Terms of Service</h1>
            <p className="text-neutral-400 mb-8">The Traders • Last updated: January 25, 2026</p>

            <div className="prose prose-invert max-w-none">
              <section className="mb-8">
                <h2 className="text-2xl font-semibold text-white mb-4">1. Eligibility and Account Registration</h2>
                <ul className="list-disc list-inside text-neutral-300 space-y-2">
                  <li>You must be at least 18 years old and legally able to enter into a contract.</li>
                  <li>You are responsible for maintaining the confidentiality of your credentials.</li>
                  <li>You agree to provide accurate information and keep it up to date.</li>
                </ul>
              </section>

              <section className="mb-8">
                <h2 className="text-2xl font-semibold text-white mb-4">2. Not Investment Advice</h2>
                <p className="text-neutral-300">
                  The Services provide informational content, analytics, and tools. We are not registered investment advisors,
                  broker-dealers, or financial planners, and we do not provide personalized investment, tax, or legal advice.
                  You are solely responsible for your trading decisions.
                </p>
              </section>

              <section className="mb-8">
                <h2 className="text-2xl font-semibold text-white mb-4">3. Risk Disclosure</h2>
                <p className="text-neutral-300">
                  Trading involves substantial risk and is not suitable for all investors. Past performance does not guarantee
                  future results. You may lose some or all of your invested capital.
                </p>
              </section>

              <section className="mb-8">
                <h2 className="text-2xl font-semibold text-white mb-4">4. Service Availability and Changes</h2>
                <p className="text-neutral-300">
                  We do not guarantee uninterrupted or error-free operation. The Services may be modified, suspended, or
                  discontinued at any time without liability to you.
                </p>
              </section>

              <section className="mb-8">
                <h2 className="text-2xl font-semibold text-white mb-4">5. Fees, Billing, and Taxes</h2>
                <ul className="list-disc list-inside text-neutral-300 space-y-2">
                  <li>Subscription fees and billing terms are disclosed at the time of purchase.</li>
                  <li>Payments are processed by third-party payment providers.</li>
                  <li>Fees are non-refundable except where required by law or explicitly stated.</li>
                  <li>You are responsible for any taxes related to your use of the Services.</li>
                </ul>
              </section>

              <section className="mb-8">
                <h2 className="text-2xl font-semibold text-white mb-4">6. Third-Party Services and Market Data</h2>
                <p className="text-neutral-300 mb-4">
                  The Services may integrate with third-party providers, including broker-dealers, exchanges, and market data
                  services. We are not responsible for third-party availability, accuracy, or reliability. Market data may be
                  delayed or inaccurate.
                </p>
                <p className="text-neutral-300">
                  <strong>No Affiliation</strong>: The Traders is not affiliated with, endorsed by, or sponsored by Alpaca Markets, Inc. ("Alpaca") or any other broker-dealer, exchange, or financial institution. Any references to Alpaca or other third-party services are for informational purposes only. We are an independent service provider and have no business relationship, partnership, or affiliation with Alpaca or any other broker or exchange.
                </p>
              </section>

              <section className="mb-8">
                <h2 className="text-2xl font-semibold text-white mb-4">7. Acceptable Use</h2>
                <ul className="list-disc list-inside text-neutral-300 space-y-2">
                  <li>Do not use the Services for unlawful activities or market manipulation.</li>
                  <li>Do not interfere with or disrupt the Services, networks, or security.</li>
                  <li>Do not reverse engineer, scrape, or extract data in violation of applicable laws or agreements.</li>
                  <li>Do not use automated means to access the Services in a manner that violates usage limits.</li>
                </ul>
              </section>

              <section className="mb-8">
                <h2 className="text-2xl font-semibold text-white mb-4">8. Intellectual Property</h2>
                <p className="text-neutral-300">
                  All content and software in the Services are owned by The Traders or its licensors and are protected by
                  intellectual property laws. You are granted a limited, non-exclusive, non-transferable, revocable license to
                  access the Services for your personal or internal business use.
                </p>
              </section>

              <section className="mb-8">
                <h2 className="text-2xl font-semibold text-white mb-4">9. User Content</h2>
                <p className="text-neutral-300">
                  If you submit content to the Services, you grant The Traders a worldwide, royalty-free license to host, store,
                  and use that content solely to provide and improve the Services.
                </p>
              </section>

              <section className="mb-8">
                <h2 className="text-2xl font-semibold text-white mb-4">10. Suspension and Termination</h2>
                <p className="text-neutral-300">
                  We may suspend or terminate your access to the Services at any time for suspected violations of these Terms,
                  security risks, or legal compliance requirements.
                </p>
              </section>

              <section className="mb-8">
                <h2 className="text-2xl font-semibold text-white mb-4">11. Disclaimers</h2>
                <p className="text-neutral-300">
                  The Services and content are provided "AS IS" and "AS AVAILABLE" without warranties of any kind, including
                  merchantability, fitness for a particular purpose, and non-infringement. We do not guarantee the accuracy,
                  timeliness, or completeness of content.
                </p>
              </section>

              <section className="mb-8">
                <h2 className="text-2xl font-semibold text-white mb-4">12. Limitation of Liability</h2>
                <p className="text-neutral-300 mb-4">
                  To the maximum extent permitted by law, The Traders and its affiliates will not be liable for any indirect,
                  incidental, special, consequential, or punitive damages, or any loss of profits, revenue, data, or business
                  opportunities.
                </p>
                <p className="text-neutral-300">
                  Our total liability for any claim related to the Services is limited to the greater of $100 or the amounts paid
                  by you to The Traders for the Services in the 12 months preceding the event giving rise to the claim.
                </p>
              </section>

              <section className="mb-8">
                <h2 className="text-2xl font-semibold text-white mb-4">13. Indemnification</h2>
                <p className="text-neutral-300">
                  You agree to indemnify and hold harmless The Traders and its affiliates from claims, damages, losses, and
                  expenses arising out of your use of the Services or violation of these Terms.
                </p>
              </section>

              <section className="mb-8">
                <h2 className="text-2xl font-semibold text-white mb-4">14. Dispute Resolution and Class Action Waiver</h2>
                <p className="text-neutral-300 mb-4">
                  Any dispute arising out of or related to these Terms or the Services will be resolved by binding arbitration
                  under the rules of the American Arbitration Association (AAA), unless prohibited by law. You and The Traders
                  waive any right to participate in a class action or class-wide arbitration.
                </p>
                <p className="text-neutral-300">
                  If you wish to opt out of arbitration, you must send a written notice to privacy@thetraders.ai within 30 days
                  of first accepting these Terms.
                </p>
              </section>

              <section className="mb-8">
                <h2 className="text-2xl font-semibold text-white mb-4">15. Governing Law</h2>
                <p className="text-neutral-300">
                  These Terms are governed by the laws of the jurisdiction in which The Traders is legally registered and
                  operates, without regard to conflict of law principles.
                </p>
              </section>

              <section className="mb-8">
                <h2 className="text-2xl font-semibold text-white mb-4">16. Changes to These Terms</h2>
                <p className="text-neutral-300">
                  We may update these Terms from time to time. Continued use of the Services after an update constitutes
                  acceptance of the revised Terms.
                </p>
              </section>

              <section>
                <h2 className="text-2xl font-semibold text-white mb-4">17. Contact</h2>
                <p className="text-neutral-300">
                  For questions about these Terms, contact us via in-app support or at privacy@thetraders.ai.
                </p>
              </section>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default TermsOfService;